import { createContext, useContext } from 'react';
import type { Types as CornerstoneTypes } from '@cornerstonejs/core';
import type { Dicom } from '@/models/dicom';
import type { SelectedImageTools } from '@/state/slices/measurementSession';

export interface GridLayout {
  rows: number;
  cols: number;
}

/** Snapshot of the viewport camera after initial setup — used to restore on reset. */
export interface ViewportInitialCamera {
  panWorld: [number, number];
  parallelScale: number;
  /** Corrected video dimensions (only for VideoViewport with aspect ratio patch). */
  videoWidth?: number;
  videoHeight?: number;
}

export interface ViewerContextValue {
  studyUuid: string;

  // Rendering engine (shared across all viewports)
  renderingEngine: CornerstoneTypes.IRenderingEngine | null;
  renderingEngineId: string;

  // Active viewport (for tool application on hover)
  activeViewportId: string | null;
  setActiveViewportId: (id: string | null) => void;
  // The tool families the active viewport's image takes
  activeImage: SelectedImageTools | null;

  // Selected viewport (for DICOM loading - distinct from hover)
  selectedViewportIndex: number;
  setSelectedViewportIndex: (index: number) => void;

  // Group navigation state
  currentGroupIndex: number;
  totalGroups: number;
  viewportCount: number;

  // Navigation actions
  goToNextGroup: () => void;
  goToPreviousGroup: () => void;
  loadDicomByGlobalIndex: (index: number) => void;
  navigateToDicomByUuid: (uuid: string) => void;
  /** Navigate to a measurement's DICOM and seek to its frame. */
  goToAnnotation: (dicomUuid: string, frameNumber: number) => void;

  // Navigation helpers
  canGoNext: boolean;
  canGoPrevious: boolean;

  // Tool state (shared across viewports)
  activeTool: string | undefined;
  changeTool: (tool: string) => void;
  deactivateTool: () => void;

  // Tool group lifecycle (MUST be called by each viewport)
  setupToolGroupForViewport: (viewportId: string) => void;
  cleanupToolGroupForViewport: (viewportId: string) => void;

  // Viewport registry - actual Cornerstone viewportIds (including instance IDs)
  registeredViewportIds: string[];
  registerViewportId: (viewportId: string) => void;
  unregisterViewportId: (viewportId: string) => void;

  // Base viewport IDs (for reference)
  viewportIds: string[];

  // Grid layout (for MultiViewerContainer)
  gridLayout: GridLayout;
  setGridLayout: (layout: GridLayout) => void;
  handleViewportClick: (viewportIndex: number) => void;
  handleToolChange: (tool: string) => void;
  assignedDicoms: (Dicom | null)[];
  /** Playback speed factor (1 = native) for a given pane slot. */
  getPaneCineSpeed: (paneIndex: number) => number;
  /** Set the playback speed factor for a single pane slot. */
  setPaneCineSpeed: (paneIndex: number, multiplier: number) => void;
  /** Resolved native FPS for a pane slot (0 until known). */
  getPaneNativeFps: (paneIndex: number) => number;
  /** Report a pane slot's resolved native FPS (from the loaded video). */
  setPaneNativeFps: (paneIndex: number, fps: number) => void;
  autoplay: boolean;
  startFrame: number | undefined;
  setAutoplayEnabled: (v: boolean) => void;
  setStartFrame: (v: number | undefined) => void;
  isInitialized: boolean;
  engineError: Error | null;

  /** Per-DICOM VOI (window/level) persistence map. Keyed by DICOM UUID. */
  voiMapRef: React.RefObject<Map<string, { lower: number; upper: number }>>;

  /** Per-viewport initial camera snapshot. Keyed by Cornerstone viewport ID. */
  viewportInitialCameraRef: React.RefObject<Map<string, ViewportInitialCamera>>;

  /** Reset W/L, pan, and zoom to defaults on all visible viewports. */
  resetViewport: () => void;

  /** Map of pane slot index to pinned Dicom. */
  pinnedPanes: ReadonlyMap<number, Dicom>;
  /** True when no visible pane is unpinned — normal navigation is blocked. */
  allPanesPinned: boolean;
  /** Pin a pane so normal navigation skips it. Never changes its content. */
  pinPane: (paneIndex: number) => void;
  /** Unpin a pane. It keeps its DICOM until the next navigation replaces it. */
  unpinPane: (paneIndex: number) => void;
  /** Shift+drag placement: show a DICOM in a pane (pinned or not), pin and focus it. */
  placeDicomInPane: (paneIndex: number, dicom: Dicom) => void;
  /** A pane finished loading a DICOM; a failed Shift+drag placement is undone. */
  reportPaneLoadResult: (
    paneIndex: number,
    dicomUuid: string,
    loaded: boolean
  ) => void;
  /** Resolve a Dicom by UUID from the current study sorted list. */
  getDicomByUuid: (uuid: string) => Dicom | undefined;
  /** 1-based display number per DICOM uuid, from the unfiltered sorted order. */
  dicomNumberByUuid: ReadonlyMap<string, number>;
  /** UUIDs of DICOMs currently shown in non-pinned pane slots on the active page. */
  activeDicomUuids: ReadonlySet<string>;

  // View filtering (AP-441)
  /** Study dicoms after the View Filter + View Group (filtered + tier-prioritized). */
  filteredSortedDicoms: Dicom[];
  /** Distinct view labels present in the study — the View Filter's options. */
  availableViews: string[];
  /** Canonical view labels selected in the View Filter (empty = no view filter). */
  selectedViews: string[];
  setSelectedViews: (views: string[]) => void;
  /** Selected clinical View Group id, or null. */
  selectedGroupId: string | null;
  setSelectedGroupId: (id: string | null) => void;
}

export const ViewerContext = createContext<ViewerContextValue | null>(null);

export function useViewerContext(): ViewerContextValue {
  const context = useContext(ViewerContext);
  if (!context) {
    throw new Error(
      'useViewerContext must be used within a ViewerContext.Provider'
    );
  }
  return context;
}
